/* Payments — compact redesign (Claude Design handoff "Payments Redesign").
   One page with four tabs: Accounts, AI Usage, Transactions and Referral program. The
   wallet balance, the Top up CTA and the Volume discount sit in the header so they're visible on every tab;
   Top up, Telegram notifications and the plan conditions open as dialogs. */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageTransition } from '../components/PageTransition';
import { PageHeading, PageTabs } from '../components/PageHeader';
import { api } from '../api/client';
import type { SubscriptionNotificationSettings } from '../api/client';
import { C, FONT } from '../components/ChatAssistant/compact/tokens';
import { font as f } from '../components/ChatAssistant/compact/style';
import { ErrorLine, Icon } from '../components/ChatAssistant/compact/kit';
import { usePaymentsData } from '../components/Payments/usePaymentsData';
import { AccountsTab } from '../components/Payments/AccountsTab';
import { UsageTab } from '../components/Payments/UsageTab';
import { TransactionsTab } from '../components/Payments/TransactionsTab';
import { ReferralTab } from '../components/Payments/ReferralTab';
import { TopUpDialog } from '../components/Payments/TopUpDialog';
import { NotificationsDialog } from '../components/Payments/NotificationsDialog';
import { useNtfSummary } from '../components/Payments/useNtfSummary';
import { ConditionsDialog } from '../components/Payments/ConditionsDialog';
import { VolumeBadge } from '../components/Payments/VolumeDiscount';
import { WalletTermsDialog } from '../components/Payments/WalletTermsDialog';
import { ConfirmDialog, Modal, ModalFoot, ModalHead, PrimaryBtn, type ConfirmSpec } from '../components/Payments/ui';
import { LOW_WALLET_USD, usd, type PlanKey } from '../components/Payments/model';

type Tab = 'accounts' | 'usage' | 'transactions' | 'referral';
const TABS: { id: Tab; icon: string }[] = [
  { id: 'accounts', icon: 'users' },
  { id: 'usage', icon: 'bar-chart-2' },
  { id: 'transactions', icon: 'file-text' },
  { id: 'referral', icon: 'gift' },
];

export function PaymentsPage() {
  const { t } = useTranslation('paymentsCompact');
  const [params, setParams] = useSearchParams();
  const data = usePaymentsData();
  const { summary } = data;

  const tabParam = params.get('tab') as Tab | null;
  const tab: Tab = TABS.some((x) => x.id === tabParam) ? (tabParam as Tab) : 'accounts';
  const setTab = (next: Tab) => setParams(next === 'accounts' ? {} : { tab: next }, { replace: true });

  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);
  const [topUp, setTopUp] = useState<{ amount?: number; code?: string } | null>(null);
  const [cond, setCond] = useState<PlanKey | null>(null);
  const [ntfOpen, setNtfOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [ntf, setNtf] = useState<SubscriptionNotificationSettings | null>(null);
  const ntfSummary = useNtfSummary(ntf);

  useEffect(() => {
    api.getNotificationSettings().then(setNtf).catch(() => setNtf(null));
  }, []);

  // Deep links from the old routes: /payments?topup=1[&code=…] opens the Top up dialog.
  useEffect(() => {
    if (params.get('topup')) {
      setTopUp({ code: params.get('code') || undefined });
      const next = new URLSearchParams(params);
      next.delete('topup');
      next.delete('code');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const showError = useCallback((m: string) => setError(m), []);
  const openNotice = useCallback((title: string, body: string) => setNotice({ title, body }), []);

  const wallet = (summary?.wallet_balance_cents ?? 0) / 100;
  const low = !!summary && wallet < LOW_WALLET_USD;
  const accounts = summary?.accounts ?? [];
  const noAssistUsd = (accounts.find((a) => a.no_assistant_price_cents > 0)?.no_assistant_price_cents ?? 0) / 100;

  return (
    <PageTransition>
      <div className="cpt-root pay-root" style={{ minHeight: '100%', background: C.page, color: C.text, fontFamily: FONT, borderRadius: 12 }}>
        <div className="pay-shell" style={{ width: '100%', padding: '20px 24px 96px', display: 'flex', flexDirection: 'column', gap: 14, boxSizing: 'border-box' }}>
          {/* Header: title, notifications, wallet + the main CTA */}
          <div className="pay-head" style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            {/* Top-aligned so the title sits where it does on the other pages, not centred on the wallet card. */}
            <div style={{ flex: '1 1 320px', minWidth: 260 }}>
              <PageHeading title={t('title')} subtitle={t('subtitle')} />
            </div>
            {/* One right-aligned group: when it can't fit beside the title it wraps inside itself, so the wallet
                stays top-right under the other two instead of dropping to an orphan row of its own. */}
            <div className="pay-head-actions" style={{ flex: '0 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setNtfOpen(true)}
                className="cpt-hover-border cpt-hover-text"
                style={{ height: 32, padding: '0 11px', display: 'flex', alignItems: 'center', gap: 7, background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, color: 'var(--om-body)', font: f(500, 12.5), cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <Icon name="send" size={13} color={C.purpleLt} />
                {t('ntf.button')}
                <span style={{ font: f(400, 11.5), color: C.muted }}>{ntfSummary}</span>
              </button>
              {summary?.volume_discount && <VolumeBadge vd={summary.volume_discount} />}
              <div className="pay-wallet" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 8px 8px 14px', background: 'color-mix(in srgb, var(--om-accent) 12%, transparent)', border: '1px solid var(--om-accent-deep)', borderRadius: 10, boxSizing: 'border-box' }}>
                <span style={{ width: 34, height: 34, flex: 'none', borderRadius: 8, background: C.tint20, color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="credit-card" size={17} />
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.sub }}>{t('wallet.label')}</span>
                    <button type="button" onClick={() => setTermsOpen(true)} className="cpt-hover-text" style={{ padding: 0, border: 0, background: 'transparent', display: 'flex', alignItems: 'center', gap: 3, font: f(500, 11), color: C.purpleLt, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <Icon name="info" size={11} />
                      {t('wallet.terms')}
                    </button>
                  </span>
                  <span style={{ font: f(700, 20, 1.1), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{summary ? usd(wallet) : '—'}</span>
                  <span className="pay-wallet-sub" style={{ font: f(500, 11), color: low ? C.purplePale : C.muted, whiteSpace: 'nowrap' }}>{low ? t('wallet.low') : t('wallet.sub')}</span>
                </div>
                <PrimaryBtn icon="plus" height={40} onClick={() => setTopUp({})} className="pay-wallet-cta" style={{ padding: '0 18px', borderRadius: 8, font: f(600, 14), boxShadow: '0 6px 20px color-mix(in srgb, var(--om-accent) 35%, transparent)' }}>
                  {t('topUpWallet')}
                </PrimaryBtn>
              </div>
            </div>
          </div>

          <PageTabs
            tabs={TABS.map((x) => ({ id: x.id, icon: x.icon, label: t(`tabs.${x.id}`), badge: x.id === 'accounts' && summary ? accounts.length : undefined }))}
            active={tab}
            onChange={(id) => setTab(id as Tab)}
          />

          {(error || data.loadError) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1 }}><ErrorLine>{error || data.loadError || t('errors.load')}</ErrorLine></div>
              {error && (
                <button type="button" aria-label={t('close')} onClick={() => setError('')} className="cpt-hover-icon" style={{ width: 28, height: 28, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="x" size={14} />
                </button>
              )}
            </div>
          )}

          {tab === 'accounts' && (
            <AccountsTab
              // The CRM's No-plan lock links here for one account (?account=<username or id>).
              initialQuery={params.get('account') ?? ''}
              data={data}
              wallet={wallet}
              onTopUp={(amount) => setTopUp({ amount })}
              onConditions={setCond}
              confirm={setConfirm}
              notice={openNotice}
              setError={showError}
            />
          )}
          {tab === 'usage' && <UsageTab data={data} initialMode={params.get('mode') === 'od' ? 'od' : 'sub'} confirm={setConfirm} setError={showError} />}
          {tab === 'transactions' && <TransactionsTab ledger={summary?.ledger ?? []} accounts={accounts} loading={data.loading} />}
          {tab === 'referral' && <ReferralTab setError={showError} />}
        </div>
      </div>

      {termsOpen && <WalletTermsDialog onClose={() => setTermsOpen(false)} />}
      {ntfOpen && <NotificationsDialog initial={ntf} onClose={() => setNtfOpen(false)} onSaved={setNtf} />}
      {topUp && (
        <TopUpDialog
          wallet={wallet}
          initialAmount={topUp.amount}
          initialCode={topUp.code}
          onClose={() => setTopUp(null)}
          onDone={() => void data.reloadSummary().catch(() => null)}
        />
      )}
      {cond && (
        <ConditionsDialog
          plan={cond}
          onPlan={setCond}
          ladder={data.turn30?.rate_ladder ?? []}
          volume={summary?.volume_discount}
          baseRateUsd={data.metered?.base_rate_usd}
          noAssistUsd={noAssistUsd}
          wallet={wallet}
          onClose={() => setCond(null)}
          onTopUp={() => { setCond(null); setTopUp({}); }}
        />
      )}
      {confirm && <ConfirmDialog spec={confirm} onClose={() => setConfirm(null)} />}
      {notice && (
        <Modal width={400} onClose={() => setNotice(null)}>
          <ModalHead icon="info" title={notice.title} onClose={() => setNotice(null)} />
          <div style={{ padding: '4px 16px 16px', font: f(400, 12.5, 1.5), color: C.sub }}>{notice.body}</div>
          <ModalFoot>
            <span style={{ flex: 1 }} />
            <PrimaryBtn height={30} onClick={() => setNotice(null)}>{t('ok')}</PrimaryBtn>
          </ModalFoot>
        </Modal>
      )}
    </PageTransition>
  );
}
